import * as Sentry from '@sentry/angular';
import { ErrorEvent } from '@sentry/angular';
import { escapeRegExp } from 'lodash-es';

import {
	IDENTITY_IS_SUPPORT_ACCESS,
	scrubSentryEvent,
	SENTRY_FRAME_CONTEXT_LINES,
	SENTRY_IGNORE_ERRORS,
	SENTRY_PII_DENY_TERMS,
} from '@efficiently/models';
import { Mut } from '@efficiently/shared-util-types';

import { scrubSentryLog } from './sentry-log-scrub.util';
import { type OpenFeatureHookRegistrar, registerOpenFeatureSentryAttribution } from './sentry-openfeature.util';

const SENTRY_REPLAY_SAMPLE_RATE = 0.05;
const SENTRY_TRACES_SAMPLE_RATE = 0.05;
const SENTRY_PROFILES_SAMPLE_RATE = 0.02;
const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = 0.1;
const SENTRY_REPLAY_CANVAS_MAX_SIZE = [640, 640] as const;
const KONVA_STAGE_LAYERS_WARNING_REGEXP = /^Konva warning: The stage has \d+ layers/;
const APOLLO_MESSAGE_REGEXP = /^Apollo\./;
const TRACE_PROPAGATION_PROTOCOLS: readonly string[] = ['http:', 'https:'];

/**
 * Depth at which the view-hierarchy walk stops descending.
 *
 * The integration walks the whole DOM synchronously for every error event, and
 * the schedule grid nests deeply enough for an unbounded walk to be noticeable.
 * The top levels carry the diagnostic value — which page, dialog and panel were
 * on screen — so the deep leaves are pruned.
 */
const SENTRY_VIEW_HIERARCHY_MAX_DEPTH = 20;

interface ClientSentryBuildConfig {
	readonly release: string;
	readonly commitId: string;
	readonly sentryDist: string;
	readonly replaysSampleRate: number | string | undefined;
	readonly replaysOnErrorSampleRate: number | string | undefined;
	readonly tracesSampleRate: number | string | undefined;
	readonly profilesSampleRate: number | string | undefined;
}

export interface ClientSentryEnvironment {
	readonly name: string;
	readonly endpoints: {
		readonly sentryDsn: string;
	};
	readonly build: ClientSentryBuildConfig;
	readonly clarity?: {
		readonly apiEndpoint: string;
	};
}

export interface ClientSentryOptions {
	readonly app: string;
	readonly environment: ClientSentryEnvironment;
	readonly windowRef?: Window;
	/**
	 * The OpenFeature global API — `OpenFeature` from `@openfeature/web-sdk` — for feature-flag
	 * attribution (BR-4).
	 *
	 * Optional because an application may have no flag consumer at all (`client-portal` has none today),
	 * and because this library must not depend on a flag SDK to be initialised. **Omitting it ships blank
	 * attribution**: the flag section of an event is simply absent, silently, which is exactly the failure
	 * `registerOpenFeatureSentryAttribution` documents. An application that registers a provider must
	 * pass it here in the same breath.
	 */
	readonly openFeature?: OpenFeatureHookRegistrar;
}

function getSampleRate(value: number | string | undefined, fallback: number): number {
	if (typeof value === 'number') {
		return value;
	}

	if (typeof value === 'string' && value !== '') {
		const parsed = Number(value);

		return Number.isNaN(parsed) ? fallback : parsed;
	}

	return fallback;
}

/**
 * Attaches feature-flag attribution when the application wired OpenFeature, and does nothing when it did
 * not (BR-4).
 *
 * A named function rather than an `if` inside `initClientSentry`: the guard is the whole content of the
 * decision, and stating it here keeps the one place that decides whether attribution exists readable —
 * `initClientSentry` is already a long function whose branch count is watched.
 */
function registerFlagAttribution(openFeature: OpenFeatureHookRegistrar | undefined): void {
	if (!openFeature) {
		return;
	}

	registerOpenFeatureSentryAttribution(openFeature);
}

/**
 * Trace-propagation targets for the Clarity API.
 *
 * @param clarity - the environment's Clarity block, absent in an application without Clarity.
 * @returns one pattern matching exactly the origin of `apiEndpoint` when it is an absolute `http:` or
 * `https:` URL; an empty list for an absent, empty, relative or unparsable endpoint. Never throws.
 */
function getClarityTracePropagationTargets(clarity: ClientSentryEnvironment['clarity']): readonly RegExp[] {
	const endpoint = URL.parse(clarity?.apiEndpoint ?? '');

	if (!endpoint || !TRACE_PROPAGATION_PROTOCOLS.includes(endpoint.protocol)) {
		return [];
	}

	// eslint-disable-next-line security/detect-non-literal-regexp -- an anchored literal of the escaped origin
	return [new RegExp(`^${escapeRegExp(endpoint.origin)}/`)];
}

export function isClientSentryEnabled(options: Pick<ClientSentryOptions, 'environment' | 'windowRef'>): boolean {
	const windowRef = options.windowRef ?? window;

	return Boolean(options.environment.endpoints.sentryDsn) && windowRef.location.hostname !== 'localhost';
}

export function initClientSentry(options: ClientSentryOptions): void {
	const windowRef = options.windowRef ?? window;

	if (!isClientSentryEnabled({ environment: options.environment, windowRef })) {
		return;
	}

	const isSupportLoggedIn = Boolean(windowRef.localStorage.getItem(IDENTITY_IS_SUPPORT_ACCESS));
	const replaysSessionSampleRate = getSampleRate(
		options.environment.build.replaysSampleRate,
		SENTRY_REPLAY_SAMPLE_RATE,
	);
	const replaysOnErrorSampleRate = getSampleRate(
		options.environment.build.replaysOnErrorSampleRate,
		SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
	);
	const tracesSampleRate = getSampleRate(options.environment.build.tracesSampleRate, SENTRY_TRACES_SAMPLE_RATE);
	const profilesSampleRate = getSampleRate(options.environment.build.profilesSampleRate, SENTRY_PROFILES_SAMPLE_RATE);

	Sentry.init({
		dsn: options.environment.endpoints.sentryDsn,
		environment: options.environment.name,
		release: options.environment.build.release,
		dist: options.environment.build.sentryDist,
		replaysOnErrorSampleRate,
		replaysSessionSampleRate,
		tracesSampler: samplingContext => {
			if (String(samplingContext.attributes?.['url.full'] ?? '').includes('fonts.gstatic.com')) {
				return 0;
			}

			if (samplingContext.parentSampled !== undefined) {
				return samplingContext.parentSampled ? tracesSampleRate : 0;
			}

			return tracesSampleRate;
		},
		profileSessionSampleRate: profilesSampleRate,
		profileLifecycle: 'trace',
		// v11 defaults this to `true`; a synthetic stack on non-Error captures would regroup issues.
		attachStacktrace: false,
		initialScope: {
			tags: {
				app: options.app,
				commit_id: options.environment.build.commitId,
				environment: options.environment.name,
				release: options.environment.build.release,
			},
		},
		ignoreErrors: SENTRY_IGNORE_ERRORS,
		// Logs bypass `beforeSend`, so they need their own scrubber. Whether any are sent is decided
		// by `ClientLogSentryTransport.isEnabled()` (`sentryLogsEnabled`): v11 removed `enableLogs`.
		beforeSendLog: log => scrubSentryLog(log),
		dataCollection: {
			userInfo: false,
			cookies: { deny: [...SENTRY_PII_DENY_TERMS] },
			httpHeaders: {
				request: { deny: [...SENTRY_PII_DENY_TERMS] },
				response: { deny: [...SENTRY_PII_DENY_TERMS] },
			},
			httpBodies: [],
			urlQueryParams: { deny: [...SENTRY_PII_DENY_TERMS] },
			graphQL: { document: true, variables: false },
			genAI: { inputs: false, outputs: false },
			databaseQueryData: false,
			queues: false,
			stackFrameVariables: true,
			frameContextLines: SENTRY_FRAME_CONTEXT_LINES,
		},
		beforeSend(event: Mut<ErrorEvent>) {
			const sessionId = Sentry.getIsolationScope()?.getSession?.()?.sid;
			const message = event.message ?? '';

			event.tags = {
				...event.tags,
				...(sessionId ? { session_id: String(sessionId) } : {}),
				...(isSupportLoggedIn ? { is_support_logged_in: 'true' } : {}),
			};

			if (KONVA_STAGE_LAYERS_WARNING_REGEXP.test(message)) {
				return null;
			}

			if (APOLLO_MESSAGE_REGEXP.test(message)) {
				return null;
			}

			return scrubSentryEvent(event);
		},
		tracePropagationTargets: [
			/^https:\/\/.*\.app\.efficiently\.com\//,
			'https://app.efficiently.com',
			...getClarityTracePropagationTargets(options.environment.clarity),
		],
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.browserProfilingIntegration(),
			Sentry.browserSessionIntegration({ lifecycle: 'page' }),
			Sentry.contextLinesIntegration(),
			Sentry.extraErrorDataIntegration({
				depth: 5,
			}),
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
				ignore: ['.ds-sentry-ignore, [ds-data-sentry-ignore]', 'input[type="password"]'],
				block: ['.ds-sentry-block', '[ds-data-sentry-block]', '.sensitive-data'],
				networkDetailAllowUrls: [],
				networkRequestHeaders: [],
			}),
			Sentry.replayCanvasIntegration({
				quality: 'low',
				maxCanvasSize: SENTRY_REPLAY_CANVAS_MAX_SIZE,
			}),
			Sentry.graphqlClientIntegration({
				endpoints: [/\/graphql$/],
			}),
			Sentry.viewHierarchyIntegration({
				onElement: ({ depth }) =>
					depth !== undefined && depth >= SENTRY_VIEW_HIERARCHY_MAX_DEPTH ? 'skip' : {},
			}),
			Sentry.fetchStreamPerformanceIntegration(),
		],
		maxBreadcrumbs: 50,
	});

	// `initialScope.tags` reach errors only; streamed spans and logs are searched by attributes.
	Sentry.getGlobalScope().setAttributes({ app: options.app, commit_id: options.environment.build.commitId });

	registerFlagAttribution(options.openFeature);
}

export function suppressConsoleInProduction(): void {
	/* eslint-disable no-console */
	console.log = (): void => {};
	console.info = (): void => {};
	console.warn = (): void => {};
	console.debug = (): void => {};
	/* eslint-enable no-console */
}
